import { RedirectBoundary as CometRedirectBoundary, type useAuth } from '@zg-rentals/auth-comet';
import type { ComponentProps, ComponentType } from 'react';
import { ErrorPage } from '@zg-rentals/error-page-components';
import pathsManifest from '../utils/pathsManifest';

export function RedirectBoundary(props: ComponentProps<typeof CometRedirectBoundary>) {
  const paths = pathsManifest();
  const isDevelopment = process.env.NODE_ENV === 'development';

  const ctaText = isDevelopment ? 'Return to SLW Dev Tools Page' : 'Return to Zillow Rental Manager';
  const ctaUrl = isDevelopment ? paths.devToolsPage : paths.homePage;

  const onError = (errorMessage: string, errorCode: number) => (
    <ErrorPage
      errorMessage={errorMessage}
      errorDetails={`Error code: ${errorCode}`}
      action={<a href={ctaUrl}>{ctaText}</a>}
    />
  );

  return (
    <CometRedirectBoundary
      host={process.env.NEXT_PUBLIC_RENTAL_MANAGER_PREFIX_URL}
      onError={onError('unknown error', 500)}
      onUnauthorized={() => onError('unauthorized', 401)}
      {...props}
    />
  );
}

export function withRedirectBoundary<
  Props extends { user: Exclude<ReturnType<typeof useAuth>['user'], null | undefined> },
>(Component: ComponentType<Props>, boundaryProps?: Omit<ComponentProps<typeof RedirectBoundary>, 'children'>) {
  const WithRedirectBoundary: ComponentType<Props> = (props: Props) => (
    <RedirectBoundary {...boundaryProps}>{(user) => <Component {...props} user={user} />}</RedirectBoundary>
  );

  WithRedirectBoundary.displayName = `WithRedirectBoundary(${Component.displayName})`;

  return WithRedirectBoundary;
}
